import { IconBusinesses, IconKeywords, IconSettings, IconWebsiteAudit, IconSEOIntelligence, IconGoogleSearch, IconGoogleBusiness, IconAIAssistant, IconReports } from '../components/icons';
// =============================================================
// BusinessDashboardPage  Phase 2 SEO Project Dashboard
// Displays live business foundation data & disconnected SEO integrations
// =============================================================

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  googleApi,
  intelligenceApi,
  automationApi,
  Business,
  BusinessService,
  BusinessTargetLocation,
  WebsiteAudit,
  GoogleStatusResponse,
  SearchConsoleSummaryDto,
  GoogleBusinessSummaryDto,
  SearchConsolePropertyDto,
  GoogleBusinessConnectionDto,
  OpportunityEngineSummaryDto,
  AutomationSummaryDto,
  AutomationExecutionDto,
} from '../lib/api';

function formatWorkflowName(key: string): string {
  switch (key) {
    case 'website_audit':
      return 'Website Audit';
    case 'search_console_sync':
      return 'Search Console Sync';
    case 'google_business_profile_sync':
      return 'Google Business Profile Sync';
    case 'opportunity_generation':
      return 'Opportunity Generation';
    case 'ai_opportunity_analysis':
      return 'AI Opportunity Analysis';
    case 'weekly_seo_summary':
      return 'Weekly SEO Summary';
    default:
      return key
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}

function formatTriggerType(type: string): string {
  switch (type) {
    case 'SCHEDULED':
      return 'Scheduled (Cron)';
    case 'MANUAL':
      return 'Manual';
    case 'SYSTEM':
      return 'System Event';
    case 'WEBHOOK':
      return 'Webhook';
    default:
      return type;
  }
}

function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  } catch {
    return dateString;
  }
}

export default function BusinessDashboardPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const parsedBizId = businessId ? parseInt(businessId, 10) : undefined;

  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Quick Add Service modal state
  const [showAddService, setShowAddService] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceDesc, setNewServiceDesc] = useState('');
  const [addingService, setAddingService] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);

  // Quick Add Target Location modal state
  const [showAddTargetLoc, setShowAddTargetLoc] = useState(false);
  const [newTargetName, setNewTargetName] = useState('');
  const [newTargetCity, setNewTargetCity] = useState('');
  const [newTargetState, setNewTargetState] = useState('');
  const [addingTargetLoc, setAddingTargetLoc] = useState(false);
  const [targetLocError, setTargetLocError] = useState<string | null>(null);

  // Phase 3: Website SEO Audit state
  const [latestAudit, setLatestAudit] = useState<WebsiteAudit | null>(null);
  const [auditErrorsCount, setAuditErrorsCount] = useState<number>(0);
  const [auditWarningsCount, setAuditWarningsCount] = useState<number>(0);
  const [showAuditConfirmModal, setShowAuditConfirmModal] = useState(false);
  const [targetWebsite, setTargetWebsite] = useState('');
  const [startingAudit, setStartingAudit] = useState(false);
  const [auditActionError, setAuditActionError] = useState<string | null>(null);

  // Phase 4: Google Integrations state
  const [googleStatus, setGoogleStatus] = useState<GoogleStatusResponse | null>(null);
  const [gscProperty, setGscProperty] = useState<SearchConsolePropertyDto | null>(null);
  const [gscSummary, setGscSummary] = useState<SearchConsoleSummaryDto | null>(null);
  const [gbpConnection, setGbpConnection] = useState<GoogleBusinessConnectionDto | null>(null);
  const [gbpSummary, setGbpSummary] = useState<GoogleBusinessSummaryDto | null>(null);
  const [syncingGsc, setSyncingGsc] = useState(false);
  const [syncingGbp, setSyncingGbp] = useState(false);

  // Phase 5A: SEO Intelligence summary
  const [intelligenceSummary, setIntelligenceSummary] = useState<OpportunityEngineSummaryDto | null>(null);

  // Phase 6: Automation operations summary & recent executions
  const [automationSummary, setAutomationSummary] = useState<AutomationSummaryDto | null>(null);
  const [recentExecutions, setRecentExecutions] = useState<AutomationExecutionDto[]>([]);

  const fetchBusiness = useCallback(async () => {
    if (!parsedBizId) return;
    setLoading(true);
    setError(null);
    setGscProperty(null);
    setGscSummary(null);
    setGbpConnection(null);
    setGbpSummary(null);
    setLatestAudit(null);
    setIntelligenceSummary(null);
    setAutomationSummary(null);
    setRecentExecutions([]);

    try {
      const [bizData, audits, iSummary, autoSummary, autoExecutions] = await Promise.all([
        businessApi.get(parsedBizId),
        businessApi.listAudits(parsedBizId).catch(() => [] as WebsiteAudit[]),
        intelligenceApi.getSummary(parsedBizId).catch(() => null),
        automationApi.getSummary(parsedBizId).catch(() => null),
        automationApi.listExecutions({ businessId: parsedBizId, limit: 5 }).catch(() => ({ items: [] })),
      ]);

      setBusiness(bizData);
      setTargetWebsite(bizData.website ?? '');
      setIntelligenceSummary(iSummary);
      setAutomationSummary(autoSummary);
      setRecentExecutions(autoExecutions?.items ?? []);

      if (audits && audits.length > 0) {
        const latest = audits[0];
        setLatestAudit(latest);
        if (latest.status === 'COMPLETED') {
          try {
            const findings = await businessApi.getAuditFindings(parsedBizId, latest.id);
            setAuditErrorsCount(findings.filter((f) => f.severity === 'ERROR').length);
            setAuditWarningsCount(findings.filter((f) => f.severity === 'WARNING').length);
          } catch {
            // ignore
          }
        }
      } else {
        setLatestAudit(null);
      }

      // Phase 4: Fetch Google Integrations
      try {
        const gStatus = await googleApi.getStatus(parsedBizId);
        setGoogleStatus(gStatus);

        if (gStatus.connected) {
          // Fetch GSC
          try {
            const gscData = await googleApi.getSearchConsoleProperties(parsedBizId);
            const connProp = (gscData as any)?.connectedProperty ?? (gscData as any)?.selectedProperty ?? null;
            setGscProperty(connProp);
            if (connProp) {
              const summary = await googleApi.getSearchConsoleMetrics(parsedBizId);
              setGscSummary(summary);
            }
          } catch {
            // ignore
          }

          // Fetch GBP
          try {
            const gbpData = await googleApi.getGoogleBusinessLocations(parsedBizId);
            const connLoc = (gbpData as any)?.connectedLocation ?? (gbpData as any)?.selectedLocation ?? null;
            setGbpConnection(connLoc);
            if (connLoc) {
              const summary = await googleApi.getGoogleBusinessMetrics(parsedBizId);
              setGbpSummary(summary);
            }
          } catch {
            // ignore
          }
        }
      } catch {
        // Google not configured or unavailable
      }
    } catch (err: unknown) {
      console.error('Failed to load business dashboard', err);
      const msg = err instanceof Error ? err.message : 'Business not found or access denied';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [parsedBizId]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  useEffect(() => {
    if (parsedBizId) {
      localStorage.setItem('active_dashboard_business_id', String(parsedBizId));
      window.dispatchEvent(new CustomEvent('active-business-change', { detail: parsedBizId }));
    }
  }, [parsedBizId]);

  // Live polling when crawl is actively running
  useEffect(() => {
    if (!latestAudit || !parsedBizId) return;
    const isRunning =
      latestAudit.status === 'QUEUED' ||
      latestAudit.status === 'CRAWLING' ||
      latestAudit.status === 'ANALYZING';

    if (!isRunning) return;

    const interval = setInterval(async () => {
      try {
        const audits = await businessApi.listAudits(parsedBizId);
        if (audits.length > 0) {
          const fresh = audits[0];
          setLatestAudit(fresh);
          if (fresh.status === 'COMPLETED') {
            const findings = await businessApi.getAuditFindings(parsedBizId, fresh.id);
            setAuditErrorsCount(findings.filter((f) => f.severity === 'ERROR').length);
            setAuditWarningsCount(findings.filter((f) => f.severity === 'WARNING').length);
          }
        }
      } catch (err) {
        console.error('Failed to poll audit status', err);
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [latestAudit, parsedBizId]);

  // Handler to start an audit
  async function handleStartAudit() {
    if (!parsedBizId) return;
    try {
      setStartingAudit(true);
      setAuditActionError(null);
      setShowAuditConfirmModal(false);

      const audit = await businessApi.startAudit(parsedBizId, targetWebsite.trim() || undefined);
      setLatestAudit(audit);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start audit';
      setAuditActionError(msg);
    } finally {
      setStartingAudit(false);
    }
  }

  // Quick Add Service handler
  async function handleCreateService(e: React.FormEvent) {
    e.preventDefault();
    if (!parsedBizId || !newServiceName.trim()) return;

    try {
      setAddingService(true);
      setServiceError(null);
      await businessApi.createService(parsedBizId, {
        name: newServiceName.trim(),
        description: newServiceDesc.trim() || undefined,
      });
      setShowAddService(false);
      setNewServiceName('');
      setNewServiceDesc('');
      fetchBusiness();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add service';
      setServiceError(msg);
    } finally {
      setAddingService(false);
    }
  }

  // Quick Add Target Location handler
  async function handleCreateTargetLocation(e: React.FormEvent) {
    e.preventDefault();
    if (!parsedBizId || !newTargetName.trim()) return;

    try {
      setAddingTargetLoc(true);
      setTargetLocError(null);
      await businessApi.createTargetLocation(parsedBizId, {
        name: newTargetName.trim(),
        city: newTargetCity.trim() || newTargetName.trim(),
        state: newTargetState.trim() || undefined,
        country: 'India',
      });
      setShowAddTargetLoc(false);
      setNewTargetName('');
      setNewTargetCity('');
      setNewTargetState('');
      fetchBusiness();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add target location';
      setTargetLocError(msg);
    } finally {
      setAddingTargetLoc(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout title="Business Dashboard" subtitle="Loading SEO project...">
        <div className="card" style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: 12 }}></div>
          Loading business details and SEO project status...
        </div>
      </DashboardLayout>
    );
  }

  if (error || !business) {
    return (
      <DashboardLayout title="Error" subtitle="Business Access Denied">
        <div className="card" style={{ padding: 36, textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}></div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 8 }}>
            Unable to Load Business
          </h2>
          <p style={{ color: '#64748b', marginBottom: 20 }}>
            {error ?? 'This business does not belong to your organization or does not exist.'}
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/businesses')}>
             Back to All Businesses
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const primaryLocation = business.locations?.[0];
  const servicesList: BusinessService[] = business.services ?? [];
  const targetLocationsList: BusinessTargetLocation[] = business.targetLocations ?? [];
  const isOnboardingComplete = business.onboarding?.completed ?? false;
  const isAuditRunning =
    latestAudit?.status === 'QUEUED' ||
    latestAudit?.status === 'CRAWLING' ||
    latestAudit?.status === 'ANALYZING';

  return (
    <DashboardLayout
      title={business.name}
      subtitle="Local SEO Project & Business Profile"
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Businesses', to: '/businesses' },
        { label: business.name },
      ]}
    >
      {/* Top Business Profile Header Card */}
      <div className="card business-header-hero">
        <div className="hero-left">
          <div className="biz-avatar"><IconBusinesses style={{width: 32, height: 32, color: '#0ea5e9'}} /></div>
          <div>
            <div className="biz-title-row">
              <h1 className="biz-heading">{business.name}</h1>
              {isOnboardingComplete ? (
                <span className="badge badge-green"> Setup Complete</span>
              ) : (
                <Link
                  to={`/businesses/${business.id}/onboarding`}
                  className="badge badge-amber clickable-badge"
                  title="Click to resume setup"
                >
                   Setup Incomplete (Resume)
                </Link>
              )}
            </div>

            <div className="biz-meta-chips">
              {business.industry && (
                <span className="meta-chip">
                  <span className="chip-icon"><IconSettings style={{width: 14, height: 14}} /></span> {business.industry}
                </span>
              )}
              {primaryLocation?.city && (
                <span className="meta-chip">
                  <span className="chip-icon"><IconGoogleBusiness style={{width: 14, height: 14}} /></span> {primaryLocation.city}, {primaryLocation.state}
                </span>
              )}
              {business.website && (
                <a
                  href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="meta-chip meta-link"
                >
                  <span className="chip-icon"><IconGoogleSearch style={{width: 14, height: 14}} /></span> {business.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              {business.phone && (
                <span className="meta-chip">
                  <span className="chip-icon"><IconBusinesses style={{width: 14, height: 14}} /></span> {business.phone}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="hero-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link
            to={`/businesses/${business.id}/integrations`}
            className="btn btn-primary btn-sm"
            style={{ textDecoration: 'none' }}
          >
             Google Integrations
          </Link>
          <Link
            to={`/businesses/${business.id}/onboarding`}
            className="btn btn-secondary btn-sm"
          >
             Edit Onboarding
          </Link>
        </div>
      </div>

      {/* SECTION 1: REAL BUSINESS INFORMATION */}
      <div className="section-header">
        <div className="section-title-wrap">
          <h2 className="section-title">
            <span className="indicator live" /> REAL BUSINESS INFORMATION
          </h2>
          <span className="section-badge-verified">Verified Tenant Data</span>
        </div>
        <p className="section-subtitle">
          Configured attributes stored in your database for local search indexing and geo-targeting.
        </p>
      </div>

      <div className="dashboard-real-grid">
        {/* Card A: Location Details */}
        <div className="card info-card">
          <div className="info-card-header">
            <div className="info-icon-badge"><IconBusinesses style={{width: 24, height: 24, color: '#0ea5e9'}} /></div>
            <div>
              <h3 className="info-card-title">Primary Business Location</h3>
              <p className="info-card-desc">Used for Google Maps pin positioning</p>
            </div>
          </div>

          <div className="info-card-body">
            {primaryLocation ? (
              <div className="location-detail-list">
                <div className="detail-row">
                  <span className="detail-k">Location Name:</span>
                  <span className="detail-v font-semibold">{primaryLocation.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-k">Street Address:</span>
                  <span className="detail-v">{primaryLocation.address || 'Not specified'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-k">City, State:</span>
                  <span className="detail-v">
                    {primaryLocation.city ?? ''}, {primaryLocation.state ?? ''}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-k">Postal Code & Country:</span>
                  <span className="detail-v">
                    {primaryLocation.postalCode ? `${primaryLocation.postalCode}, ` : ''}
                    {primaryLocation.country ?? 'India'}
                  </span>
                </div>
                {primaryLocation.latitude && primaryLocation.longitude && (
                  <div className="detail-row">
                    <span className="detail-k">GPS Coordinates:</span>
                    <span className="detail-v code-pill">
                      {primaryLocation.latitude}, {primaryLocation.longitude}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-subcard">
                <p>No physical address configured.</p>
                <Link to={`/businesses/${business.id}/onboarding`} className="btn-link">
                  + Add Address
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Card B: Industry & Audience Settings */}
        <div className="card info-card">
          <div className="info-card-header">
            <div className="info-icon-badge"><IconKeywords style={{width: 24, height: 24, color: '#8b5cf6'}} /></div>
            <div>
              <h3 className="info-card-title">Categories & Market Focus</h3>
              <p className="info-card-desc">Google category mapping & buyer persona</p>
            </div>
          </div>

          <div className="info-card-body">
            <div className="detail-row">
              <span className="detail-k">Primary Industry:</span>
              <span className="detail-v font-semibold">
                {business.settings?.primaryIndustry ?? business.industry ?? 'Not configured'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-k">Primary Category:</span>
              <span className="detail-v">
                {business.settings?.primaryCategory ?? 'General'}
              </span>
            </div>
            <div className="detail-row-block">
              <span className="detail-k">Secondary Categories:</span>
              <div className="tags-container" style={{ marginTop: 6 }}>
                {business.settings?.secondaryCategories && business.settings.secondaryCategories.length > 0 ? (
                  business.settings.secondaryCategories.map((c) => (
                    <span key={c} className="badge badge-blue">
                      {c}
                    </span>
                  ))
                ) : (
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>None configured</span>
                )}
              </div>
            </div>
            {business.settings?.targetAudience && (
              <div className="detail-row" style={{ marginTop: 8 }}>
                <span className="detail-k">Target Audience:</span>
                <span className="detail-v">{business.settings.targetAudience}</span>
              </div>
            )}
          </div>
        </div>

        {/* Card C: Defined Services */}
        <div className="card info-card" style={{ gridColumn: 'span 1' }}>
          <div className="info-card-header">
            <div className="info-icon-badge"><IconSettings style={{width: 24, height: 24, color: '#f59e0b'}} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="info-card-title">Services ({servicesList.length})</h3>
                <button
                  id="add-service-modal-btn"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAddService(true)}
                >
                  + Add Service
                </button>
              </div>
              <p className="info-card-desc">Core offerings targeted for local ranking</p>
            </div>
          </div>

          <div className="info-card-body">
            {servicesList.length === 0 ? (
              <div className="empty-subcard">
                <p>No services registered yet.</p>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAddService(true)}
                >
                  + Add First Service
                </button>
              </div>
            ) : (
              <div className="services-stacked-list">
                {servicesList.map((svc) => (
                  <div key={svc.id} className="service-list-item">
                    <div className="service-name-row">
                      <span className="service-bullet"></span>
                      <strong className="service-name">{svc.name}</strong>
                    </div>
                    {svc.description && (
                      <p className="service-desc">{svc.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Card D: Target Locations */}
        <div className="card info-card" style={{ gridColumn: 'span 1' }}>
          <div className="info-card-header">
            <div className="info-icon-badge"><IconGoogleBusiness style={{width: 24, height: 24, color: '#10b981'}} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="info-card-title">Target Locations ({targetLocationsList.length})</h3>
                <button
                  id="add-target-loc-modal-btn"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAddTargetLoc(true)}
                >
                  + Add Target
                </button>
              </div>
              <p className="info-card-desc">Surrounding cities and regional markets</p>
            </div>
          </div>

          <div className="info-card-body">
            {targetLocationsList.length === 0 ? (
              <div className="empty-subcard">
                <p>No target locations registered.</p>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAddTargetLoc(true)}
                >
                  + Add First Target Location
                </button>
              </div>
            ) : (
              <div className="target-locs-container">
                {targetLocationsList.map((tLoc) => (
                  <div key={tLoc.id} className="target-loc-pill">
                    <span className="loc-marker"></span>
                    <span className="loc-name">{tLoc.name}</span>
                    {tLoc.state && <span className="loc-state">({tLoc.state})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 2: WEBSITE SEO CRAWLER & AUDIT (PHASE 3 LIVE) */}
      <div className="section-header" style={{ marginTop: 40 }}>
        <div className="section-title-wrap">
          <h2 className="section-title">
            <span className="indicator live" /> WEBSITE SEO AUDIT
          </h2>
          <span className="section-badge-verified">Deterministic Engine Active</span>
        </div>
        <p className="section-subtitle">
          On-page crawl & deterministic analysis of technical SEO, local signals, schema markup, and content health.
        </p>
      </div>

      <div className="dashboard-seo-widget">
        <div className="seo-widget-top">
          <div className="seo-widget-headline">
            <div className="seo-widget-icon"><IconWebsiteAudit style={{width: 24, height: 24, color: '#0ea5e9'}} /></div>
            <div>
              <h3 className="seo-widget-title">Website SEO Health</h3>
              <div className="seo-widget-url-chip">
                <span>Target:</span>
                <strong>{business.website || 'No website registered'}</strong>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              id="run-seo-audit-btn"
              className="btn btn-primary"
              onClick={() => setShowAuditConfirmModal(true)}
              disabled={startingAudit || isAuditRunning}
            >
              {latestAudit ? ' Re-Run SEO Audit' : ' Run SEO Audit'}
            </button>
            {latestAudit && (
              <Link
                to={`/businesses/${business.id}/seo`}
                className="btn btn-secondary"
              >
                View Detailed Report 
              </Link>
            )}
          </div>
        </div>

        {/* If crawl is running: Progress Stepper */}
        {isAuditRunning && (
          <div className="crawl-stepper">
            <div className={`stepper-step ${latestAudit?.status === 'QUEUED' ? 'active' : 'done'}`}>
              <div className="step-dot" />
              <span>Queued</span>
            </div>
            <div className="stepper-line" />
            <div
              className={`stepper-step ${
                latestAudit?.status === 'CRAWLING'
                  ? 'active'
                  : latestAudit?.status === 'ANALYZING' || latestAudit?.status === 'COMPLETED'
                  ? 'done'
                  : ''
              }`}
            >
              <div className="step-dot" />
              <span>Crawling</span>
            </div>
            <div className="stepper-line" />
            <div
              className={`stepper-step ${
                latestAudit?.status === 'ANALYZING'
                  ? 'active'
                  : latestAudit?.status === 'COMPLETED'
                  ? 'done'
                  : ''
              }`}
            >
              <div className="step-dot" />
              <span>Analyzing</span>
            </div>
            <div className="stepper-line" />
            <div className={`stepper-step ${latestAudit?.status === 'COMPLETED' ? 'done' : ''}`}>
              <div className="step-dot" />
              <span>Completed</span>
            </div>
          </div>
        )}

        {auditActionError && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            <span></span>
            <span>{auditActionError}</span>
          </div>
        )}

        {/* Stats Grid */}
        <div className="seo-widget-grid">
          <div className="seo-stat-box">
            <span className="seo-stat-label">SEO Score</span>
            <span className="seo-stat-val">
              {latestAudit?.overallScore !== null && latestAudit?.overallScore !== undefined
                ? `${latestAudit.overallScore} / 100`
                : 'Not Audited'}
            </span>
          </div>
          <div className="seo-stat-box">
            <span className="seo-stat-label">Audit Status</span>
            <span className="seo-stat-val" style={{ fontSize: '1.1rem' }}>
              {latestAudit?.status ? (
                <span
                  className={`badge ${
                    latestAudit.status === 'COMPLETED'
                      ? 'badge-green'
                      : latestAudit.status === 'FAILED'
                      ? 'badge-red'
                      : 'badge-blue'
                  }`}
                >
                  {latestAudit.status}
                </span>
              ) : (
                <span className="badge badge-gray">PENDING</span>
              )}
            </span>
          </div>
          <div className="seo-stat-box">
            <span className="seo-stat-label">Pages Crawled</span>
            <span className="seo-stat-val">{latestAudit?.pagesCrawled ?? 0}</span>
          </div>
          <div className="seo-stat-box">
            <span className="seo-stat-label">Issues Detected</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span className="tag-err font-semibold">
                {auditErrorsCount} errors
              </span>
              <span className="tag-warn font-semibold">
                {auditWarningsCount} warnings
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.8rem',
            color: '#64748b',
          }}
        >
          <span>
            {latestAudit?.completedAt
              ? `Last audited: ${new Date(latestAudit.completedAt).toLocaleString()}`
              : latestAudit?.startedAt
              ? `Started: ${new Date(latestAudit.startedAt).toLocaleString()}`
              : 'No previous audits found. Run your first audit to inspect site SEO.'}
          </span>
          <Link
            to={`/businesses/${business.id}/seo`}
            style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
          >
            Open Comprehensive Audit & Page Explorer 
          </Link>
        </div>
      </div>

      {/* SECTION: SEO OPPORTUNITIES (PHASE 5A LIVE) */}
      <div className="section-header" style={{ marginTop: 40 }}>
        <div className="section-title-wrap">
          <h2 className="section-title">
            <span className="indicator live" /> SEO OPPORTUNITIES
          </h2>
          <span className="section-badge-verified">Intelligence Engine Active</span>
        </div>
        <p className="section-subtitle">
          Deterministic recommendations converted from website audits, Search Console queries, and Google Business Profile signals.
        </p>
      </div>

      <div
        className="card"
        style={{
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.8rem',
                flexShrink: 0,
              }}><IconSEOIntelligence style={{width: 32, height: 32, color: '#2563eb'}} /></div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                {intelligenceSummary?.openOpportunities ?? 0} Open Opportunities
              </h3>
              <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.88rem' }}>
                {intelligenceSummary && intelligenceSummary.totalOpportunities > 0
                  ? `Opportunity Score: ${intelligenceSummary.opportunityScore}/100  Total generated: ${intelligenceSummary.totalOpportunities}`
                  : 'Ready to generate opportunities from stored website & Google performance data'}
              </p>
            </div>
          </div>

          <Link
            to={`/businesses/${business.id}/intelligence`}
            className="btn btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              fontWeight: 600,
              fontSize: '0.92rem',
              textDecoration: 'none',
              backgroundColor: '#2563eb',
              color: '#ffffff',
              borderRadius: '8px',
            }}
          >
            <span>View SEO Intelligence </span>
          </Link>
        </div>

        {intelligenceSummary && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '12px',
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid #f1f5f9',
            }}
          >
            <div style={{ padding: '12px', background: '#fee2e2', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase' }}>
                Critical
              </div>
              <strong style={{ fontSize: '1.4rem', color: '#991b1b' }}>{intelligenceSummary.criticalCount}</strong>
            </div>

            <div style={{ padding: '12px', background: '#ffedd5', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase' }}>
                High Priority
              </div>
              <strong style={{ fontSize: '1.4rem', color: '#ea580c' }}>{intelligenceSummary.highCount}</strong>
            </div>

            <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase' }}>
                Medium Priority
              </div>
              <strong style={{ fontSize: '1.4rem', color: '#d97706' }}>{intelligenceSummary.mediumCount}</strong>
            </div>

            <div style={{ padding: '12px', background: '#f1f5f9', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                Low Priority
              </div>
              <strong style={{ fontSize: '1.4rem', color: '#475569' }}>{intelligenceSummary.lowCount}</strong>
            </div>
          </div>
        )}
      </div>

      {/* SECTION 3: GOOGLE & SEARCH ENGINE INTEGRATIONS */}
      <div className="section-header" style={{ marginTop: 40 }}>
        <div className="section-title-wrap">
          <h2 className="section-title">
            <span className={`indicator ${googleStatus?.connected ? 'live' : 'pending'}`} /> GOOGLE & SEARCH ENGINE INTEGRATIONS
          </h2>
          <span className={googleStatus?.connected ? 'section-badge-verified' : 'section-badge-unconnected'}>
            {googleStatus?.connected ? 'OAuth 2.0 Connected' : 'Google Not Connected'}
          </span>
        </div>
        <p className="section-subtitle">
          Real-time search console queries and Google Business Profile performance data. Every metric displays its explicit data source.
        </p>
      </div>

      <div className="seo-integrations-grid">
        {/* Integration 1: Google Business Profile */}
        <div className="card integration-card">
          <div className="integration-card-top">
            <div className="integration-icon-wrap" style={{ background: '#eef2ff' }}>
              <span style={{ fontSize: '1.5rem' }}><IconGoogleBusiness style={{width: 24, height: 24, color: '#4285F4'}} /></span>
            </div>
            <span
              className={`badge status-badge ${
                googleStatus?.connected && gbpConnection?.status === 'CONNECTED'
                  ? 'badge-green'
                  : 'badge-amber'
              }`}
            >
              {googleStatus?.connected && gbpConnection?.status === 'CONNECTED'
                ? 'Connected'
                : 'Not connected'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <h3 className="integration-title" style={{ margin: 0 }}>Google Business Profile</h3>
          </div>
          <span
            style={{
              fontSize: '0.68rem',
              color: '#166534',
              background: '#dcfce7',
              padding: '1px 6px',
              borderRadius: '3px',
              fontWeight: 600,
              display: 'inline-block',
              marginBottom: '8px',
            }}
          >
            Data Source: Google Business Profile
          </span>

          {googleStatus?.connected && gbpConnection?.status === 'CONNECTED' ? (
            <div style={{ marginTop: '8px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#334155' }}>
                Location: <strong>{gbpConnection.googleLocationName || 'Active Listing'}</strong>
              </p>

              {gbpSummary ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Search Views</span>
                    <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>{gbpSummary.searchImpressions.toLocaleString()}</strong>
                  </div>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Maps Views</span>
                    <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>{gbpSummary.mapsImpressions.toLocaleString()}</strong>
                  </div>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Direct Calls</span>
                    <strong style={{ fontSize: '1.05rem', color: '#16a34a' }}>{gbpSummary.callClicks.toLocaleString()}</strong>
                  </div>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Directions</span>
                    <strong style={{ fontSize: '1.05rem', color: '#2563eb' }}>{gbpSummary.directionRequests.toLocaleString()}</strong>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '12px' }}>
                  Location linked. Ready to sync 28-day performance metrics.
                </p>
              )}
            </div>
          ) : (
            <p className="integration-desc">
              Sync your official Google Maps listing, operating hours, direct phone calls, and search views.
            </p>
          )}

          <div className="integration-footer">
            <Link
              to={`/businesses/${business.id}/integrations`}
              className="btn btn-secondary btn-sm full-width"
              style={{ textAlign: 'center', textDecoration: 'none' }}
            >
              {googleStatus?.connected && gbpConnection?.status === 'CONNECTED'
                ? 'Manage GBP Connection '
                : 'Connect Business Profile '}
            </Link>
          </div>
        </div>

        {/* Integration 2: Google Search Console */}
        <div className="card integration-card">
          <div className="integration-card-top">
            <div className="integration-icon-wrap" style={{ background: '#f5f3ff' }}>
              <span style={{ fontSize: '1.5rem' }}><IconGoogleSearch style={{width: 24, height: 24, color: '#34A853'}} /></span>
            </div>
            <span
              className={`badge status-badge ${
                googleStatus?.connected && gscProperty ? 'badge-green' : 'badge-amber'
              }`}
            >
              {googleStatus?.connected && gscProperty ? 'Connected' : 'Not connected'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <h3 className="integration-title" style={{ margin: 0 }}>Google Search Console</h3>
          </div>
          <span
            style={{
              fontSize: '0.68rem',
              color: '#1e40af',
              background: '#dbeafe',
              padding: '1px 6px',
              borderRadius: '3px',
              fontWeight: 600,
              display: 'inline-block',
              marginBottom: '8px',
            }}
          >
            Data Source: Google Search Console
          </span>

          {googleStatus?.connected && gscProperty ? (
            <div style={{ marginTop: '8px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#334155' }}>
                Property: <strong>{gscProperty.siteUrl}</strong>
              </p>

              {gscSummary ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Organic Clicks</span>
                    <strong style={{ fontSize: '1.05rem', color: '#16a34a' }}>{gscSummary.totalClicks.toLocaleString()}</strong>
                  </div>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Impressions</span>
                    <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>{gscSummary.totalImpressions.toLocaleString()}</strong>
                  </div>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Avg CTR</span>
                    <strong style={{ fontSize: '1.05rem', color: '#7c3aed' }}>{((gscSummary.averageCtr ?? 0) * 100).toFixed(1)}%</strong>
                  </div>
                  <div style={{ padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.68rem', color: '#64748b', display: 'block' }}>Avg Position</span>
                    <strong style={{ fontSize: '1.05rem', color: '#b45309' }}>{(gscSummary.averagePosition ?? 0).toFixed(1)}</strong>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '12px' }}>
                  Property selected. Ready to sync 28-day search performance.
                </p>
              )}
            </div>
          ) : (
            <p className="integration-desc">
              Monitor real search impressions, local click-through rates, top queries, and target page performance.
            </p>
          )}

          <div className="integration-footer">
            <Link
              to={`/businesses/${business.id}/integrations`}
              className="btn btn-secondary btn-sm full-width"
              style={{ textAlign: 'center', textDecoration: 'none' }}
            >
              {googleStatus?.connected && gscProperty
                ? 'Manage GSC Connection '
                : 'Connect Search Console '}
            </Link>
          </div>
        </div>

        {/* Integration 3: Website SEO Audit (Active Link) */}
        <div className="card integration-card">
          <div className="integration-card-top">
            <div className="integration-icon-wrap" style={{ background: '#ecfeff' }}>
              <span style={{ fontSize: '1.5rem' }}><IconWebsiteAudit style={{width: 24, height: 24, color: '#0ea5e9'}} /></span>
            </div>
            <span
              className={`badge status-badge ${
                latestAudit?.status === 'COMPLETED'
                  ? 'badge-green'
                  : latestAudit?.status === 'CRAWLING' || latestAudit?.status === 'ANALYZING'
                  ? 'badge-blue'
                  : 'badge-gray'
              }`}
            >
              {latestAudit?.status ?? 'Not analyzed'}
            </span>
          </div>

          <h3 className="integration-title">Website SEO Audit</h3>
          <p className="integration-desc">
            Deterministic crawler for Local Business schema, meta descriptions, title tags, NAP consistency, and mobile speed.
          </p>

          <div className="integration-footer">
            <Link
              to={`/businesses/${business.id}/seo`}
              className="btn btn-primary btn-sm full-width"
              style={{ textAlign: 'center', textDecoration: 'none' }}
            >
              {latestAudit ? 'View Audit Results ' : 'Launch Crawler '}
            </Link>
          </div>
        </div>

        {/* Integration 4: Keyword Tracking */}
        <div className="card integration-card">
          <div className="integration-card-top">
            <div className="integration-icon-wrap" style={{ background: '#fef3c7' }}>
              <span style={{ fontSize: '1.5rem' }}><IconKeywords style={{width: 24, height: 24, color: '#d97706'}} /></span>
            </div>
            <span className="badge badge-gray status-badge">Not configured</span>
          </div>

          <h3 className="integration-title">Keyword Tracking</h3>
          <p className="integration-desc">
            Track search rank positions across your target cities ({targetLocationsList.map((t) => t.name).join(', ') || 'Gurugram, Delhi'}) in the Google Maps 3-Pack.
          </p>

          <div className="integration-footer">
            <button
              className="btn btn-secondary btn-sm full-width"
              disabled
              title="Scheduled for keyword engine phase"
            >
              [Configure later]
            </button>
          </div>
        </div>
      </div>

      {/* SECTION 4: AUTOMATION OPERATIONS HEALTH (PHASE 6 LIVE) */}
      <div className="section-header" style={{ marginTop: 40 }}>
        <div className="section-title-wrap">
          <h2 className="section-title">
            <span className={`indicator ${automationSummary && automationSummary.failedToday > 0 ? 'pending' : 'live'}`} /> AUTOMATION OPERATIONS HEALTH
          </h2>
          <span className={automationSummary && automationSummary.failedToday > 0 ? 'section-badge-unconnected' : 'section-badge-verified'}>
            {automationSummary && automationSummary.failedToday > 0 ? 'Review Needed' : 'Telemetry Active'}
          </span>
        </div>
        <p className="section-subtitle">
          Real-time execution status of background website audits, Google syncs, and AI-driven SEO opportunity workflows.
        </p>
      </div>

      <div
        className="card"
        style={{
          padding: 24,
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          marginBottom: 32,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 14,
            marginBottom: 20,
          }}
        >
          <div style={{ padding: '14px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase' }}>
              Completed Today
            </span>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#15803d', marginTop: 4 }}>
              {automationSummary ? automationSummary.completedToday : 0}
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: automationSummary && automationSummary.failedToday > 0 ? '#fef2f2' : '#f8fafc',
              borderRadius: 8,
              border: `1px solid ${automationSummary && automationSummary.failedToday > 0 ? '#fecaca' : '#e2e8f0'}`,
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: automationSummary && automationSummary.failedToday > 0 ? '#991b1b' : '#64748b',
                textTransform: 'uppercase',
              }}
            >
              Failed Today
            </span>
            <div
              style={{
                fontSize: '1.6rem',
                fontWeight: 800,
                color: automationSummary && automationSummary.failedToday > 0 ? '#dc2626' : '#64748b',
                marginTop: 4,
              }}
            >
              {automationSummary ? automationSummary.failedToday : 0}
            </div>
          </div>

          <div style={{ padding: '14px 16px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>
              Currently Running
            </span>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#2563eb', marginTop: 4 }}>
              {automationSummary ? automationSummary.running : 0}
            </div>
          </div>

          <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
              Queued / Stale
            </span>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
              {automationSummary ? automationSummary.queued + automationSummary.staleRunning : 0}
            </div>
          </div>
        </div>

        <div>
          <h4 style={{ margin: '0 0 12px', fontSize: '0.92rem', fontWeight: 700, color: '#334155' }}>
            Recent Automation Executions
          </h4>

          {recentExecutions.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                background: '#f8fafc',
                borderRadius: 8,
                color: '#64748b',
                fontSize: '0.88rem',
              }}
            >
              No automation executions recorded yet for this business.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b' }}>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Workflow</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Trigger</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Executed</th>
                  </tr>
                </thead>
                <tbody>
                  {recentExecutions.map((exec) => (
                    <tr key={exec.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a' }}>
                        {formatWorkflowName(exec.workflowKey)}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>
                        {formatTriggerType(exec.triggerType)}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span
                          className={`badge ${
                            exec.status === 'COMPLETED'
                              ? 'badge-green'
                              : exec.status === 'RUNNING'
                              ? 'badge-blue'
                              : exec.status === 'FAILED'
                              ? 'badge-red'
                              : 'badge-amber'
                          }`}
                          style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                        >
                          {exec.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>
                        {formatRelativeTime(exec.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* QUICK ADD SERVICE MODAL */}
      {showAddService && (
        <div className="modal-backdrop">
          <div className="modal-box" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3 className="modal-title">Add New Service</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddService(false)}
              >
                
              </button>
            </div>

            <form onSubmit={handleCreateService}>
              {serviceError && (
                <div className="alert alert-error" style={{ marginBottom: 14 }}>
                  <span></span>
                  <span>{serviceError}</span>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="new-svc-name">
                  Service Name <span className="required-star">*</span>
                </label>
                <input
                  id="new-svc-name"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Stage Lighting Rental"
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  autoFocus
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="new-svc-desc">
                  Description
                </label>
                <textarea
                  id="new-svc-desc"
                  className="form-textarea"
                  rows={3}
                  placeholder="Optional details about this service"
                  value={newServiceDesc}
                  onChange={(e) => setNewServiceDesc(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddService(false)}
                  disabled={addingService}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  id="submit-service-btn"
                  className="btn btn-primary"
                  disabled={addingService || !newServiceName.trim()}
                >
                  {addingService ? 'Saving...' : 'Add Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QUICK ADD TARGET LOCATION MODAL */}
      {showAddTargetLoc && (
        <div className="modal-backdrop">
          <div className="modal-box" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3 className="modal-title">Add Target Location</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddTargetLoc(false)}
              >
                
              </button>
            </div>

            <form onSubmit={handleCreateTargetLocation}>
              {targetLocError && (
                <div className="alert alert-error" style={{ marginBottom: 14 }}>
                  <span></span>
                  <span>{targetLocError}</span>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="new-target-name">
                  Location / City Name <span className="required-star">*</span>
                </label>
                <input
                  id="new-target-name"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Manesar"
                  value={newTargetName}
                  onChange={(e) => {
                    setNewTargetName(e.target.value);
                    if (!newTargetCity) setNewTargetCity(e.target.value);
                  }}
                  autoFocus
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="new-target-city">
                    City
                  </label>
                  <input
                    id="new-target-city"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Manesar"
                    value={newTargetCity}
                    onChange={(e) => setNewTargetCity(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="new-target-state">
                    State / Region
                  </label>
                  <input
                    id="new-target-state"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Haryana"
                    value={newTargetState}
                    onChange={(e) => setNewTargetState(e.target.value)}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddTargetLoc(false)}
                  disabled={addingTargetLoc}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  id="submit-target-loc-btn"
                  className="btn btn-primary"
                  disabled={addingTargetLoc || !newTargetName.trim()}
                >
                  {addingTargetLoc ? 'Saving...' : 'Add Target Location'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RUN SEO AUDIT CONFIRMATION MODAL */}
      {showAuditConfirmModal && (
        <div className="modal-backdrop">
          <div className="modal-box" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3 className="modal-title">Run Website SEO Audit</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAuditConfirmModal(false)}
              >
                
              </button>
            </div>

            <div className="modal-body-content">
              <p style={{ color: '#334155', marginBottom: 16, lineHeight: 1.5 }}>
                This will crawl up to <strong>50 public pages</strong> on this website.
              </p>

              <div className="form-group">
                <label className="form-label" htmlFor="audit-confirm-url">
                  Target Website URL <span className="required-star">*</span>
                </label>
                <input
                  id="audit-confirm-url"
                  type="text"
                  className="form-input"
                  value={targetWebsite}
                  onChange={(e) => setTargetWebsite(e.target.value)}
                  placeholder="https://example.com"
                  required
                />
                <span className="form-hint">
                  Crawler only accesses public HTTP/HTTPS pages and respects robots.txt.
                </span>
              </div>

              <div className="safety-notice-box">
                <span style={{ fontSize: '1rem' }}></span>
                <span style={{ fontSize: '0.78rem', color: '#0369a1' }}>
                  <strong>SSRF Safe:</strong> Private IP ranges, localhost, and cloud metadata endpoints are strictly blocked.
                </span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAuditConfirmModal(false)}
                disabled={startingAudit}
              >
                Cancel
              </button>
              <button
                type="button"
                id="confirm-run-audit-btn"
                className="btn btn-primary"
                onClick={handleStartAudit}
                disabled={startingAudit || !targetWebsite.trim()}
              >
                {startingAudit ? 'Starting...' : 'Start Crawl & Audit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
